import type { HumanTask } from '@paguz/contracts';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeftIcon, CheckIcon, InboxIcon, UserCheckIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { Field, Textarea } from '../../components/form-controls.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatDateTime, shortId } from '../../lib/format.ts';
import { can } from '../../lib/rbac.ts';
import { useSession } from '../../lib/session.ts';
import { TASK_PRIORITY, TASK_STATUS, TASK_TYPE } from './labels.ts';

const ORIGINATION = new Set<HumanTask['type']>(['CREDIT_DECISION', 'DOCUMENT_VALIDATION']);

/** Bandeja de tareas HITL: resolver o rechazar con nota reanuda el flujo del agente. */
export function TasksPage() {
  const session = useSession();
  const roles = session?.user.roles ?? [];
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.humanTasks,
    queryFn: api.listHumanTasks,
    refetchInterval: 10_000,
  });
  const [resolving, setResolving] = useState<{
    task: HumanTask;
    outcome: 'RESOLVED' | 'REJECTED';
  } | null>(null);
  const [note, setNote] = useState('');
  const canHandle = (t: HumanTask) =>
    t.type === 'SESSION_TAKEOVER'
      ? can(roles, 'agents:operate')
      : ORIGINATION.has(t.type)
        ? can(roles, 'hitl:origination')
        : can(roles, 'hitl:payments');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.humanTasks });
  };
  const assign = useMutation({
    mutationFn: async (task: HumanTask) => {
      const current = await api.getHumanTask(task.id);
      return api.assignHumanTask(task.id, current.version);
    },
    onSuccess: () => {
      refresh();
      toast.success('Tarea asignada a ti');
    },
    onError: (e) => toast.error(describeError(e)),
  });
  const resolve = useMutation({
    mutationFn: async (input: {
      task: HumanTask;
      outcome: 'RESOLVED' | 'REJECTED';
      note: string;
    }) => {
      const current = await api.getHumanTask(input.task.id);
      return api.resolveHumanTask(input.task.id, current.version, {
        outcome: input.outcome,
        note: input.note,
      });
    },
    onSuccess: () => {
      refresh();
      setResolving(null);
      setNote('');
      toast.success('Tarea cerrada; el agente retoma el flujo');
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const tasks = query.data?.data ?? [];
  const open = tasks.filter((t) => t.status !== 'RESOLVED' && t.status !== 'CANCELLED');
  const done = tasks.filter((t) => t.status === 'RESOLVED' || t.status === 'CANCELLED');

  return (
    <>
      <Link
        to="/agentes"
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeftIcon className="size-4" /> Agentes
      </Link>
      <PageHeader
        title="Tareas HITL"
        description="Lo que los agentes no pueden (ni deben) resolver solos: decisiones, validaciones y clientes que piden un humano."
      />
      <Panel bodyClassName="p-0">
        {query.isPending ? (
          <div className="p-5">
            <LoadingRows />
          </div>
        ) : query.isError ? (
          <div className="p-5">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : open.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={<InboxIcon />} title="Sin tareas pendientes" />
          </div>
        ) : (
          <TaskTable
            tasks={open}
            actions={(t) =>
              canHandle(t) && (
                <div className="flex justify-end gap-1.5">
                  {t.status === 'OPEN' && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={assign.isPending}
                      onClick={() => {
                        assign.mutate(t);
                      }}
                    >
                      <UserCheckIcon /> Tomar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="highlight"
                    onClick={() => {
                      setResolving({ task: t, outcome: 'RESOLVED' });
                    }}
                  >
                    <CheckIcon /> Resolver
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Rechazar"
                    onClick={() => {
                      setResolving({ task: t, outcome: 'REJECTED' });
                    }}
                  >
                    <XIcon />
                  </Button>
                </div>
              )
            }
          />
        )}
      </Panel>
      {done.length > 0 && (
        <Panel className="mt-6" title="Resueltas" bodyClassName="p-0">
          <TaskTable tasks={done} actions={() => null} />
        </Panel>
      )}
      <Dialog
        open={resolving !== null}
        onOpenChange={(o) => {
          if (!o) setResolving(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resolving?.outcome === 'RESOLVED' ? 'Resolver tarea' : 'Rechazar tarea'}
            </DialogTitle>
            <DialogDescription>
              {resolving ? TASK_TYPE[resolving.task.type] : ''}. La nota queda en la auditoría y el
              agente la usa para retomar.
            </DialogDescription>
          </DialogHeader>
          <Field label="Nota" htmlFor="task-note">
            <Textarea
              id="task-note"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </Field>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setResolving(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant={resolving?.outcome === 'REJECTED' ? 'destructive' : 'highlight'}
              disabled={note.trim().length < 5 || resolve.isPending}
              onClick={() => {
                if (resolving) resolve.mutate({ ...resolving, note: note.trim() });
              }}
            >
              {resolving?.outcome === 'RESOLVED' ? 'Resolver' : 'Rechazar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SubjectLink({ task }: { task: HumanTask }) {
  if (task.subjectType === 'agent_session')
    return (
      <Link
        to="/agentes/sesiones/$sessionId"
        params={{ sessionId: task.subjectId }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {shortId(task.subjectId)}
      </Link>
    );
  if (task.subjectType === 'application')
    return (
      <Link
        to="/expedientes/$applicationId"
        params={{ applicationId: task.subjectId }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {shortId(task.subjectId)}
      </Link>
    );
  return (
    <code className="font-mono text-xs text-muted-foreground">
      {task.subjectType} · {shortId(task.subjectId)}
    </code>
  );
}

function TaskTable({
  tasks,
  actions,
}: {
  tasks: readonly HumanTask[];
  actions: (t: HumanTask) => React.ReactNode;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tipo</TableHead>
          <TableHead>Origen</TableHead>
          <TableHead>Prioridad</TableHead>
          <TableHead>Vence</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead>Asignada a</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {tasks.map((t) => (
          <TableRow key={t.id}>
            <TableCell>
              <p className="font-semibold">{TASK_TYPE[t.type]}</p>
              <SubjectLink task={t} />
            </TableCell>
            <TableCell className="text-sm">
              {ORIGINATION.has(t.type) ? 'Originación' : 'Pagos'}
            </TableCell>
            <TableCell>
              <Badge variant={TASK_PRIORITY[t.priority]?.tone ?? 'secondary'}>
                {TASK_PRIORITY[t.priority]?.label ?? t.priority}
              </Badge>
            </TableCell>
            <TableCell className="text-sm whitespace-nowrap">
              {formatDateTime(t.dueAt)}
              {t.overdue && (
                <Badge variant="danger" className="ml-2">
                  Vencida
                </Badge>
              )}
            </TableCell>
            <TableCell>
              <Badge variant={TASK_STATUS[t.status]?.tone ?? 'secondary'}>
                {TASK_STATUS[t.status]?.label ?? t.status}
              </Badge>
            </TableCell>
            <TableCell className="text-sm">
              {t.assignedTo ?? <span className="text-muted-foreground">—</span>}
            </TableCell>
            <TableCell>{actions(t)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
